import React, { useState, useEffect, useRef, ComponentType, ReactNode, MutableRefObject } from 'react';
import compose from 'lodash/fp/compose';
import { useTestAB } from '@catalogo/core-experiments';
import { useDebounce } from '@catalogo/core-hooks/debounce';
import { setProductQty } from '@catalogo/service-americanas-product/src/cache/product';
import { withMetrics } from '@catalogo/core-metrics';
import { withProductQuantity } from '@catalogo/service-americanas-product/src/hocs/product-quantity';
import {
  handleCurrentQty,
  handleKeyPress,
  dispatchTimer,
  clearTimer,
  toggleEditing,
} from '../helpers/selector-quantity';

type SelectorQuantity = {
  experiment: string;
  error: string | null | undefined;
  currentQty: string | number;
  disableMinus: string | boolean;
  disablePlus: string | boolean;
  inputRef: MutableRefObject<null>;
  editing: boolean;
  descriptionQty: string;
  handleCurrentQty(): void;
  handleKeyPress(): void;
  toggleEditing(): void;
};

interface WithSelectorQuantityProps {
  children: ReactNode;
  productQty: number | string;
  limit: number;
  dispatchMetrics(): void;
  setSelectQuantityWasUsed(): void;
}

export const withSelectorQuantity = (Component: ComponentType) => (props: WithSelectorQuantityProps) => {
  const { productQty, limit, dispatchMetrics, setSelectQuantityWasUsed } = props;
  const inputRef = useRef(null);
  const [currentQty, setCurrentQty] = useState(productQty);
  const [error, setError] = useState<string | null>();
  const [editing, setEditing] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const debouncedQty = useDebounce(currentQty, 750);
  const maxPerOrder = 300;

  useEffect(() => {
    let errorMsg;
    let newQty;
    if (debouncedQty > maxPerOrder && limit >= maxPerOrder) {
      errorMsg = `Você pode comprar até <strong>${maxPerOrder}</strong> unidades<br/> por pedido, tá?`;
      newQty = maxPerOrder;
    } else if (debouncedQty > limit) {
      errorMsg = `A oferta tem apenas <strong>${limit}</strong> unidades<br/> disponíveis.`;
      newQty = limit;
    } else if (debouncedQty !== '') {
      setProductQty(parseInt(debouncedQty));
    }

    if (errorMsg) {
      setError(errorMsg);
      setEditing(false);
      const timer = setTimeout(dispatchTimer(setCurrentQty, setError, newQty), 3000);
      return clearTimer(timer);
    }
  }, [debouncedQty]);

  const selectorQuantityExperiment = useTestAB('CAT_032_SELECTOR_QUANTITY');

  let withSelectorQuantityProps = { experiment: selectorQuantityExperiment } as SelectorQuantity;
  if (selectorQuantityExperiment?.toUpperCase() === 'NEW') {
    withSelectorQuantityProps = {
      ...withSelectorQuantityProps,
      handleCurrentQty: handleCurrentQty(
        dispatchMetrics,
        setCurrentQty,
        currentQty,
        setSelectQuantityWasUsed,
        hasInteracted,
        setHasInteracted
      ),
      handleKeyPress,
      error,
      currentQty,
      disableMinus: error || currentQty === 1 || currentQty === '',
      disablePlus: error || currentQty === limit,
      inputRef,
      editing,
      descriptionQty: `${currentQty ?? 1} unidade${currentQty > 1 ? 's' : ''}`,
      toggleEditing: toggleEditing(editing, setEditing, inputRef),
    };
  }

  return <Component {...props} {...withSelectorQuantityProps} />;
};

export default compose(withProductQuantity, withMetrics, withSelectorQuantity);
